import { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, BellSimpleSlash, CheckCircle, X } from '@phosphor-icons/react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'push_optin_dismissed_until';
const DISMISS_DAYS = 7;
const SHOW_DELAY_MS = 12000;

function urlBase64ToUint8Array(base64) {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
}

/**
 * Subtle opt-in card asking the student to enable push notifications.
 * Only shown when:
 *   - browser supports push API
 *   - permission state is 'default' (never granted/denied)
 *   - user hasn't dismissed in the last 7 days
 *   - student already has an active service worker
 */
export default function PushOptInCard({ role }) {
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(false);
    const isInstructor = role === 'instructor';

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
        if (Notification.permission !== 'default') return;
        try {
            const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
            if (Date.now() < until) return;
        } catch { /* ignore */ }
        const t = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
        return () => window.clearTimeout(t);
    }, []);

    const dismiss = (days = DISMISS_DAYS) => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400000)); } catch { /* ignore */ }
        setVisible(false);
    };

    const enable = async () => {
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const { data } = await axios.get(`${API}/push/vapid-public-key`, { withCredentials: true });
            const pubKey = data?.public_key;
            if (!pubKey) throw new Error('No VAPID public key');

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                dismiss(); return;
            }

            const existing = await reg.pushManager.getSubscription();
            const sub = existing || await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(pubKey),
            });

            await axios.post(`${API}/push/subscribe`, sub.toJSON(), { withCredentials: true });
            setSuccess(true);
            window.setTimeout(() => { dismiss(365); }, 2200);
        } catch (e) {
            console.warn('Push subscribe failed:', e);
            dismiss();
        } finally {
            setBusy(false);
        }
    };

    if (!visible) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[55] bg-[#2D3748] border border-[#D4A537]/40 rounded-2xl shadow-2xl backdrop-blur-sm overflow-hidden"
            data-testid="push-optin-card"
        >
            <div className="px-4 py-3 sm:px-5 sm:py-4">
                <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-xl border flex-shrink-0 ${success ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300' : 'bg-[#D4A537]/15 border-[#D4A537]/40 text-[#D4A537]'}`}>
                        {success ? <CheckCircle size={20} weight="fill" /> : <Bell size={20} weight="fill" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {success ? 'Meldingen geactiveerd' : 'Blijf op de hoogte'}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                            {success
                                ? (isInstructor
                                    ? 'Je krijgt nu een melding wanneer een leerling een les annuleert of bij een nieuwe lesplanning.'
                                    : 'Je krijgt nu een melding wanneer je leskaart wordt bijgewerkt of er een nieuwe les wordt ingepland.')
                                : (isInstructor
                                    ? 'Ontvang een melding wanneer een leerling een les annuleert of bij geplande lessen.'
                                    : 'Ontvang een melding wanneer je instructeur je leskaart bijwerkt of een nieuwe les inplant.')}
                        </p>
                    </div>
                    {!success && (
                        <button
                            onClick={() => dismiss()}
                            className="text-gray-500 hover:text-gray-300 transition-colors p-1 -m-1 flex-shrink-0"
                            data-testid="push-dismiss"
                            aria-label="Sluiten"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>
                {!success && (
                    <div className="flex gap-2 mt-3">
                        <Button
                            onClick={() => dismiss()}
                            variant="ghost"
                            className="flex-1 text-gray-400 hover:text-white text-xs h-9"
                            data-testid="push-later"
                        >
                            <BellSimpleSlash size={14} className="mr-1.5" />
                            Niet nu
                        </Button>
                        <Button
                            onClick={enable}
                            disabled={busy}
                            className="flex-1 bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-semibold text-xs h-9"
                            data-testid="push-enable"
                        >
                            <Bell size={14} weight="fill" className="mr-1.5" />
                            {busy ? 'Bezig...' : 'Inschakelen'}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
